import { useEffect, useMemo, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select,
  Stack, TextField,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ErrorAlert } from '../../components/ErrorAlert';
import { useGenerateMachineQrBatch, useOperatorsList } from '../../api/queries';
import type { Operator } from '../../api/types';

/**
 * "Generate machine (cabinet) QR batch" — ARMS-only. Mints N cabinet
 * stickers FOR a specific operator. The operator picker is mandatory —
 * unlike IoT QR batches (which live in central ARMS stock), cabinet QRs
 * are operator-owned from mint.
 */
export function GenerateMachineQrBatchDialog({ open, onClose }:
  { open: boolean; onClose: () => void }) {
  const create = useGenerateMachineQrBatch();
  const opsQ = useOperatorsList();
  const operators = useQuery({
    queryKey: opsQ.queryKey,
    queryFn: opsQ.listFn,
    enabled: open,
  });

  const [code, setCode]   = useState('');
  const [count, setCount] = useState<number>(50);
  const [notes, setNotes] = useState('');
  const [intendedOperatorId, setIntendedOperatorId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCode(''); setCount(50); setNotes('');
      setIntendedOperatorId(''); setError(null);
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-pick the only operator when the list resolves to exactly one,
  // matching the IoT-batch UX where there is no operator picker at all.
  useEffect(() => {
    const list = operators.data;
    if (!intendedOperatorId && list && list.length === 1) {
      setIntendedOperatorId(list[0].id);
    }
  }, [operators.data, intendedOperatorId]);

  const valid =
    code.trim().length > 0 && count > 0 && count <= 10_000 && intendedOperatorId.length > 0;

  const operatorList = useMemo(
    () => (operators.data ?? []).filter((o: Operator) => o.status === 'active'),
    [operators.data]);

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({
        code:               code.trim(),
        notes:              notes.trim() || null,
        count,
        intendedOperatorId,
        labelPaper:         'A4',
        labelWidthMm:       70,
        labelHeightMm:      25,
        colsPerSheet:       3,
        rowsPerSheet:       8,
      });
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Generate failed: ${msg}`);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate machine (cabinet) QR batch</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <ErrorAlert title="Generate failed" message={error} />}
          <TextField
            label="Batch code"
            placeholder="MQR-2026-05-001"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            fullWidth required
            helperText="Human-readable label, must be unique across batches."
          />
          <FormControl fullWidth required>
            <InputLabel id="intendedOperator-label">Intended operator</InputLabel>
            <Select
              labelId="intendedOperator-label"
              label="Intended operator"
              value={intendedOperatorId}
              onChange={(e) => setIntendedOperatorId(e.target.value)}
            >
              {operatorList.map((o: Operator) => (
                <MenuItem key={o.id} value={o.id}>{o.displayName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Count"
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(0, parseInt(e.target.value || '0', 10)))}
            fullWidth required
            inputProps={{ min: 1, max: 10000 }}
            helperText="Between 1 and 10,000 cabinet QR pairs."
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth multiline minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!valid || create.isPending}>
          {create.isPending ? 'Generating…' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
